import ButtonGroup from '@passkeys/ui/button-group';
import type { IconName } from '@passkeys/ui/icon';

import MyPasskeyButton from '@/ui/components/my-passkey-button/index.js';
import useIsExtendedLayout from '@/ui/hooks/use-is-extended-layout';
import useIsLocked from '@/ui/hooks/use-is-locked';
import useLocation from '@/ui/hooks/use-location';
import useModal from '@/ui/hooks/use-modal';
import useNavigate from '@/ui/hooks/use-navigate';
import type { ReferrerLocationState } from '@/ui/utils/navigation';
import { createNavigateStateOptions } from '@/ui/utils/navigation';
import { getIsFeatureEnabled } from '@/utils/app-config';

interface Route {
  path: string;
  label: string;
  icon: IconName;
  navigateOptions?: { state: Record<string, unknown> };
}

const navigateOptions = createNavigateStateOptions<ReferrerLocationState>({
  referrer: 'navbar',
});

const TOKENS_ROUTE: Route = {
  path: '/tokens',
  label: 'Tokens',
  icon: 'circle-arc-left',
};

const SWAPS_ROUTE: Route = {
  path: '/swap',
  label: 'Swap',
  icon: 'arrow-left-arrow-right-large',
  navigateOptions,
};

const APPS_ROUTE: Route = {
  path: '/apps',
  label: 'Apps',
  icon: 'grid-sparkle',
};

const FIAT_ROUTE: Route = {
  path: '/buy',
  label: 'Buy',
  icon: 'currency',
  navigateOptions,
};

const COLLECTIBLES_ROUTE: Route = {
  path: '/collectibles',
  label: 'Collectibles',
  icon: 'shapes',
};

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const modal = useModal();

  const swapsEnabled = getIsFeatureEnabled('swaps');
  const fiatEnabled = getIsFeatureEnabled('fiat');
  const collectiblesEnabled = getIsFeatureEnabled('collectibles');

  const isExtendedLayout = useIsExtendedLayout();

  const routes = [
    TOKENS_ROUTE,
    swapsEnabled && SWAPS_ROUTE,
    __STANDALONE_MODE__ && APPS_ROUTE,
    fiatEnabled && FIAT_ROUTE,
    collectiblesEnabled && COLLECTIBLES_ROUTE,
  ].filter(Boolean) as Route[];

  const activeIndex = routes.findIndex((route) =>
    location.path.startsWith(route.path),
  );

  const isLocked = useIsLocked();

  return (
    <div className="fixed bottom-8 left-[50%] z-10 flex translate-x-[-50%] flex-col items-center md:static md:bottom-0 md:left-0 md:top-0 md:translate-x-0 md:border-r md:border-black/6 md:bg-white md:px-4 md:theme-dark:border-neutral-700 md:theme-dark:bg-black">
      <MyPasskeyButton variant="logo" className="mb-6 mt-8 hidden md:block" />

      <ButtonGroup
        activeIndex={activeIndex}
        buttons={routes.map(({ path, label, icon, navigateOptions }) => ({
          ariaLabel: label,
          icon,
          onClick:
            modal || isLocked
              ? undefined
              : () => navigate(path, navigateOptions),
        }))}
        floating={!isExtendedLayout}
        divided={!isExtendedLayout}
        boxed={!isExtendedLayout}
        orientation={isExtendedLayout ? 'vertical' : 'horizontal'}
        activeFramed={isExtendedLayout}
      />
    </div>
  );
}

export default Navbar;
